/* eslint-disable react/jsx-key */
import React, { useState, useEffect } from "react";
import {
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  SimpleGrid,
  Spacer,
  GridItem,
  HStack,
  Button
} from "@chakra-ui/react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  Column,
  usePagination,
  useFilters
} from "react-table";

import {
  DropdownMenu,
  FilterDropdown,
  FilterTag,
  Pagination,
  Search
} from "components";

import { LazyData, LazyFilters, FetchDataProps } from "types/datacontext";
import { useQuery, QueryKey, QueryFunction } from "@tanstack/react-query";

const placeholderData: LazyData<any> = {
  pageCount: 0,
  entryCount: 0,
  entries: []
}

interface Props {
  columns: Column<any>[];
  dataKey: QueryKey | ((props: FetchDataProps) => QueryKey);
  dataFn: QueryFunction;
  tableTitle?: string;
  defaultSortOrder?: string;
  dateFilter?: boolean;
}

const LazyDataTable: React.FC<Props> = ({ columns, dataKey, dataFn, tableTitle, defaultSortOrder }: Props) => {
  const [fetchProps, setFetchProps]= useState<FetchDataProps>({});

  const { isLoading, error, data } = useQuery({
    queryKey: typeof dataKey === 'function'? dataKey(fetchProps): dataKey,
    queryFn: dataFn,
    keepPreviousData: true,
    placeholderData,
  })

  //const getFilterDisplayValue = ({ id, value}: any) => context.filters[id]?.find(({id}) => id == value)?.displayValue;
  const getFilterDisplayValue = ({ id, value}: any) => id;

  const hiddenColumns = columns
    .map(col => col.id || col.accessor?.toString() || "")
    .filter(id => id != "id");

  const {
    setSortBy,
    getTableProps,
    getTableBodyProps,
    allColumns,
    prepareRow,
    setGlobalFilter,
    page,
    state: { globalFilter, pageIndex, filters, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    setFilter,
    setAllFilters,
  } = useTable<any>(
    {
      columns,
      data: (data as LazyData<any>).entries,
      initialState: {
        hiddenColumns
      },
      autoResetFilters: false,
      manualFilters: true,
      manualGlobalFilter: true,
      manualPagination: true,
      pageCount: (data as LazyData<any>).pageCount,
      autoResetPage: false
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  //set index for sorting options
  const [sortIndex, setSortIndex] = React.useState(0);
  const [sortOrder, setSortOrder] = React.useState<string[]>(defaultSortOrder? ["start_time"] : ["start_time", "DESC"]);
//  const [search, setSearch] = React.useState(globalFilter);

  // TODO may be bad. Get rid of Function??? possible eval-time issue?
  React.useEffect(() => {
    setFetchProps({ pageIndex, pageSize, filters, order: sortOrder, search: globalFilter});
  }, [pageIndex]);

  React.useEffect(() => {
    gotoPage(0);
    setFetchProps({ pageIndex: 0, pageSize, filters, order: sortOrder, search: globalFilter});
  }, [globalFilter, pageSize, sortOrder, filters])


  React.useEffect(() => {
    let option = sortOptions[sortIndex].label;
    let sortOption: string[];
    let descOption;

    if (option != "None") {
      if (option.includes("desc")) {
        descOption = true;
        option = option.split(" (desc)")[0];
      } else {
        descOption = false;
      }
      const c = allColumns.filter(col => option == col.Header);
      if (c.length > 0) {
        if (descOption) {
          sortOption = [c[0].id.toString(), "DESC"];
        } else {
          sortOption = [c[0].id.toString()];
        }
      } else {
        if (defaultSortOrder == "ASC") {
          sortOption = ["start_time"];
        } else {
          sortOption = ["start_time", "DESC"];
        }
        /* sortOption = ["start_time", "DESC"]; */
      }
    } else {
      if (defaultSortOrder == "ASC") {
        sortOption = ["start_time"];
      } else {
        sortOption = ["start_time", "DESC"];
      }
    }
    setSortOrder(sortOption);
    gotoPage(0);
  }, [sortIndex])

  //may need to update this so it sorts by asc for dashboard when sort option is cleared
  const sortOptions: any[] = [
    {
      label: "None",
      callback: () => setSortBy([])
    }
  ];

  allColumns
    .filter(col => !col.disableSortBy)
    .forEach(col => {
      sortOptions.push({
        label: col.render("Header")?.valueOf(),
        callback: () => setSortBy([{ id: col.id }])
      });
      sortOptions.push({
        label: col.render("Header")?.valueOf() + " (desc)",
        callback: () => setSortBy([{ id: col.id, desc: true }])
      });
    });

  const setClearAllOption = () => {
    console.log("filters from selectClearAllOption: ", filters);
    setAllFilters([]);
    //if (defaultSortOrder == "ASC") {
    //  console.log("setClearAllOption called");
    //  const stringifyDate = JSON.stringify(dateFilter);
    //  console.log("JSON.stringify: ", stringifyDate);
    //  //maybe try fetch data instead of setting the filter again
    //  fetchData({ pageIndex, pageSize, filters, order: sortOrder, search, dateFilter });
    //
    //};
  };

  const removeTag = (filter: any) => {
    if (filter.id != "start_time_range") {
      setFilter(filter.id, undefined);
    };
  };

  return (
    isLoading ? <Text>Loading...</Text> :
    <>
      <SimpleGrid columns={4} w="100%" gap={5}>
        <GridItem colSpan={2}>
          <Search setFilter={setGlobalFilter} filter={globalFilter} />
        </GridItem>
        <Spacer />
        <FilterDropdown columns={allColumns} filters={filters} />
      </SimpleGrid>
      <HStack hidden={filters.length <= 0} spacing={5}>
        {filters.map(filter => {
          return (
          filter.id == "start_time_range" ?
          (
            <FilterTag
            key={filter.id}
            label="Today's sessions"
            onClose={() => {
              removeTag(filter);
            }}
          />
          ) : (
            <FilterTag
            key={filter.id}
            label={getFilterDisplayValue(filter) || filter?.value}
            onClose={() => {
              removeTag(filter);
            }}
          />
          )
        )
        })}
        <Button
          variant="link"
          size="sm"
          fontWeight="semibold"
          onClick={setClearAllOption}
        >
          Clear All
        </Button>
      </HStack>
      <Table variant="unstyled" {...getTableProps()}>
        {(tableTitle || sortOptions.length > 1) && (
          <Thead>
            <Tr>
              <Th>
                <SimpleGrid columns={3} alignItems="center">
                  <Text>{tableTitle}</Text>
                  <Spacer />
                  {sortOptions.length > 1 && (
                    <HStack justifyContent="flex-end">
                      <Text>Sort By:</Text>

                      <DropdownMenu options={sortOptions} /* index={sortIndex} */ setIndex={setSortIndex} />
                    </HStack>
                  )}
                </SimpleGrid>
              </Th>
            </Tr>
          </Thead>
        )}
        <Tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <Td pl={0} pr={0} pb={0} {...cell.getCellProps()}>
                    {cell.render("Cell")}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Pagination
        previousPage={previousPage}
        canPreviousPage={canPreviousPage}
        nextPage={nextPage}
        canNextPage={canNextPage}
        currentPage={Number(pageIndex) + 1}
        totalPages={(data as LazyData<any>).pageCount}
        gotoPage={gotoPage}
      />
    </>
  );
};

export default LazyDataTable;
